import { apiGet, apiPost, apiPostForm, apiDownloadUrl } from './client';
import type {
  OcrUploadResponse,
  SearchResponse,
  LlmSearchResponse,
  CustomerSearchResponse,
  CustomerListResponse,
  ProductListResponse,
  SiteListResponse,
  TodokeListResponse,
  StaffListResponse,
  Upload,
  Order,
  OrderSummary,
  HistoryEntry,
  MemoryStats,
  Correction,
  PreferenceMemory,
  AffinityItem,
  SearchResult,
  VersionSummary,
  PaginatedResponse,
  User,
  UserListResponse,
  UserMeResponse,
  MasterSyncResult,
  MasterSyncHistoryRecord,
  MasterSyncTarget,
} from '../types/api';

// ── アップロード ──
export function uploadImages(files: File[], opts?: { uploader?: string; note?: string; doc_type?: string; skipSearch?: boolean }) {
  const fd = new FormData();
  for (const file of files) {
    fd.append('file', file);
  }
  if (opts?.uploader) fd.append('uploader', opts.uploader);
  if (opts?.note) fd.append('note', opts.note);
  if (opts?.doc_type) fd.append('doc_type', opts.doc_type);
  if (opts?.skipSearch) fd.append('skip_search', '1');
  return apiPostForm<Upload>('/uploads', fd);
}

export function listUploads(page = 1, perPage = 50) {
  return apiGet<PaginatedResponse<Upload>>(`/uploads?page=${page}&per_page=${perPage}`);
}

export function getUpload(id: string) {
  return apiGet<Upload>(`/uploads/${id}`);
}

export function deleteUpload(id: string) {
  return apiPost<{ status: string }>(`/uploads/${id}/delete`, {});
}

export function runUploadOcr(id: string) {
  return apiPost<OcrUploadResponse>(`/uploads/${id}/ocr`, {});
}

export function updateUploadState(id: string, updates: Record<string, unknown>) {
  return apiPost<{ status: string }>(`/uploads/${id}/state`, updates);
}

export function uploadFileUrl(id: string, fileIndex: number = 0) {
  return apiDownloadUrl(`/uploads/${id}/file/${fileIndex}`);
}

export interface ExcelSheet {
  name: string;
  rows: string[][];
}

export function getUploadSheet(id: string, fileIndex: number = 0) {
  return apiGet<{ sheets: ExcelSheet[] }>(`/uploads/${id}/sheet/${fileIndex}`);
}

// ── OCR ──
export function uploadOcr(file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return apiPostForm<OcrUploadResponse>('/ocr/upload', fd);
}

export function saveCorrections(params: {
  source_id: string;
  source_file: string;
  corrections: Correction[];
}) {
  return apiPost<{ recorded: number; source_id: string }>('/ocr/correct', params);
}

// ── 検索 ──
export function searchProducts(params: {
  query: string;
  top_k?: number;
  normalize?: boolean;
  dept_code?: string;
  warehouse_code?: string;
}) {
  return apiPost<SearchResponse>('/search', params);
}

export function llmSearchProducts(params: {
  query: string;
  top_k?: number;
  normalize?: boolean;
  source_id?: string;
}) {
  return apiPost<LlmSearchResponse>('/llm-search', params);
}

export function productLookup(code: string) {
  return apiPost<{ code: string; result: SearchResult | null }>('/product-lookup', { code });
}

export function confirmSelection(params: {
  query: string;
  selected_id: string;
  selected_name: string;
  selected_rank?: number | null;
  source_id?: string;
  top_results?: SearchResult[];
  dept_code?: string;
}) {
  return apiPost<{ status: string }>('/confirm', params);
}

// ── 顧客 ──
export function searchCustomers(query: string, page = 1, perPage = 20, departmentCode = '') {
  return apiPost<CustomerSearchResponse>('/customer-search', { query, page, per_page: perPage, department_code: departmentCode });
}

export function listCustomers(params: { q?: string; page: number; per_page: number }) {
  const qs = new URLSearchParams({
    q: params.q ?? '',
    page: String(params.page),
    per_page: String(params.per_page),
  });
  return apiGet<CustomerListResponse>(`/master/customers?${qs}`);
}

// ── 現場 ──
export function listSites(params: { tokcd: string; q?: string; page?: number; per_page?: number }) {
  const qs = new URLSearchParams({ tokcd: params.tokcd, q: params.q ?? '', page: String(params.page ?? 1), per_page: String(params.per_page ?? 10000) });
  return apiGet<SiteListResponse>(`/master/sites?${qs}`);
}

// ── 届け先 ──
export function listTodoke(params?: { q?: string }) {
  const qs = new URLSearchParams({ q: params?.q ?? '' });
  return apiGet<TodokeListResponse>(`/master/todoke?${qs}`);
}

// ── 担当者 ──
export function listStaff(params?: { q?: string }) {
  const qs = new URLSearchParams({ q: params?.q ?? '' });
  return apiGet<StaffListResponse>(`/master/staff?${qs}`);
}

// ── 倉庫・在庫 ──
export function listWarehouses() {
  return apiGet<{ total: number; items: Record<string, string>[] }>('/master/warehouses');
}

export function getWarehouseByDept(deptCode: string) {
  return apiGet<{ code: string; name: string }>(`/master/warehouse-by-dept?dept_code=${encodeURIComponent(deptCode)}`);
}

export function getWarehouseByCustomer(customerCode: string) {
  return apiGet<{ code: string; name: string }>(`/master/warehouse-by-dept?customer_code=${encodeURIComponent(customerCode)}`);
}

export function listStock(params: { q?: string; page?: number; per_page?: number; zone?: string }) {
  const qs = new URLSearchParams({
    q: params.q ?? '',
    page: String(params.page ?? 1),
    per_page: String(params.per_page ?? 50),
    zone: params.zone ?? '',
  });
  return apiGet<{ total: number; page: number; per_page: number; items: Record<string, string>[] }>(`/master/stock?${qs}`);
}

export function listProductCategories(level: number, parent?: string) {
  const qs = new URLSearchParams({ level: String(level), parent: parent ?? '' });
  return apiGet<{ items: Array<{ code: string; name: string }> }>(`/master/product-categories?${qs}`);
}

export function listProductsByCategory(category: string) {
  const qs = new URLSearchParams({ category });
  return apiGet<{ items: Array<{ id: string; name1: string; name2: string; name_editable?: boolean }> }>(
    `/master/products-by-category?${qs}`,
  );
}

/** 商品コード直打ちの入力補助: コード順で前後 span 件（＋一致/直近1件）を返す。 */
export function productsAround(code: string, span = 5) {
  const qs = new URLSearchParams({ code, span: String(span) });
  return apiGet<{ code: string; items: Array<{ id: string; name1: string; name2: string; category: string }> }>(
    `/master/products-around?${qs}`,
  );
}

export function listProducts(params: { q?: string; page: number; per_page: number; editableOnly?: boolean }) {
  const qs = new URLSearchParams({
    q: params.q ?? '',
    page: String(params.page),
    per_page: String(params.per_page),
  });
  // 名称を書き換えてよい汎用コード（名称ﾏﾆｭｱﾙ入力区分='1'）だけに絞る
  if (params.editableOnly) qs.set('editable_only', '1');
  return apiGet<ProductListResponse>(`/master/products?${qs}`);
}

// ── 注文 ──
export function saveOrder(order: Omit<Order, 'order_id' | 'created_at'>) {
  return apiPost<{ status: string; order_id: string }>('/orders/save', order);
}

export function updateOrder(orderId: string, body: { items: unknown[] }) {
  return apiPost<{ status: string; order_id: string }>(`/orders/${orderId}/update`, body);
}

export function listOrders(page = 1, perPage = 50, days = 7) {
  return apiGet<PaginatedResponse<OrderSummary>>(`/orders?page=${page}&per_page=${perPage}&days=${days}`);
}

// 受注伝票No.の突合（直近days日のerp_written/erp_error注文をJDNRWKと突合し伝票No.・状態を反映）
export function reconcileSlips(days = 7, limit = 200) {
  return apiPost<{ ok: boolean; checked: number; completed: string[]; errors: string[]; reverted?: string[]; error?: string }>(
    '/orders/reconcile-slips', { days, limit });
}

export function getOrder(orderId: string) {
  return apiGet<Order>(`/orders/${orderId}`);
}

export function deleteOrder(orderId: string) {
  return apiPost<{ status: string }>(`/orders/${orderId}/delete`, {});
}

export function orderCsvUrl(orderId: string) {
  return apiDownloadUrl(`/orders/${orderId}/csv`);
}

export function validateOrder(orderId: string) {
  return apiGet<{ warnings: Array<{ field: string; message: string; level: string }> }>(`/orders/${orderId}/validate`);
}

export function erpResend(orderId: string) {
  return apiPost<{ status: string; order_id: string; erp: { status: string; inserted: number; seqnos: string[]; slip_number: string | null } }>(`/orders/${orderId}/erp-resend`, {});
}

export function bulkCsvUrl() {
  return apiDownloadUrl('/orders/bulk-csv');
}

// ── 履歴 ──
export function getHistory(page = 1, perPage = 50) {
  return apiGet<PaginatedResponse<HistoryEntry>>(`/history?page=${page}&per_page=${perPage}`);
}

export function deleteHistory(id: string, source_id: string) {
  return apiPost<{ status: string }>('/history/delete', { id, source_id });
}

export function deleteCorrectionLog(id: string, source_id: string) {
  return apiPost<{ status: string }>('/correction-logs/delete', { id, source_id });
}

export function deleteAffinity(query: string, product_id: string) {
  return apiPost<{ status: string }>('/affinity/delete', { query, product_id });
}

// ── メモリ ──
export function getMemoryStats() {
  return apiGet<MemoryStats>('/memory/stats');
}

export function distillMemory(body: { source?: string; days?: number } = {}) {
  return apiPost<{ status: string; memory?: PreferenceMemory; error?: string }>(
    '/memory/distill',
    body,
  );
}

export function deleteMemoryEntry(params: {
  layer: string;
  key: string;
  scope: string;
  index: number;
}) {
  return apiPost<{ status: string; memory: PreferenceMemory }>('/memory/delete-entry', params);
}

export function deleteMemoryKey(params: { layer: string; key: string }) {
  return apiPost<{ status: string; memory: PreferenceMemory }>('/memory/delete-key', params);
}

export function listMemoryVersions() {
  return apiGet<VersionSummary[]>('/memory/versions');
}

export function rollbackMemoryVersion(version: number) {
  return apiPost<{ status: string; version: number; rolled_back_from: number; memory: PreferenceMemory }>(
    '/memory/rollback',
    { version },
  );
}

// ── Affinity ──
export function listAffinity(page = 1, perPage = 50) {
  return apiGet<PaginatedResponse<AffinityItem>>(`/affinity?page=${page}&per_page=${perPage}`);
}

// ── その他 ──
export function listIndexes() {
  return apiGet<string[]>('/indexes');
}

export function getSynonyms() {
  return apiGet<Record<string, string[]>>('/synonyms');
}

// ── ユーザー管理 ──
export function getMe() {
  return apiGet<UserMeResponse>('/users/me');
}

export function listUsers(params?: { role?: string; q?: string }) {
  const qs = new URLSearchParams({ role: params?.role ?? '', q: params?.q ?? '' });
  return apiGet<UserListResponse>(`/users?${qs}`);
}

// 身元（id/email）はサーバがトークンから取得するため、bodyは表示名と担当者コードのみ
export function registerUser(body: { display_name: string; staff_code: string }) {
  return apiPost<{ status: string; user: User }>('/users/register', body);
}

export function setUserRole(id: string, role: string) {
  return apiPost<{ status: string; user: User }>(`/users/${encodeURIComponent(id)}/role`, { role });
}

export function setUserEnabled(id: string, enabled: boolean) {
  return apiPost<{ status: string; user: User }>(`/users/${encodeURIComponent(id)}/enable`, { enabled });
}

export function deleteUser(id: string) {
  return apiPost<{ status: string }>('/users/delete', { id });
}

// ── マスタ同期（管理者）──
// targets: 対象マスタのキー配列（未指定/空なら全マスタ）。マスタを個別同期するとき指定。
// preview: ERP に接続して差分だけ算出（DB更新なし）
export function masterSyncPreview(targets?: string[]) {
  return apiPost<MasterSyncResult>('/master-sync/preview', targets?.length ? { targets } : {});
}

// apply: ERP から取得したマスタを Blob/Cosmos に本番反映
export function masterSyncApply(targets?: string[]) {
  return apiPost<MasterSyncResult>('/master-sync/apply', targets?.length ? { targets } : {});
}

// targets: 同期対象マスタの一覧（個別選択の候補）
export function masterSyncTargets() {
  return apiGet<{ targets: MasterSyncTarget[] }>('/master-sync/targets');
}

// history: 同期実行履歴（Timer/手動の両方、何を何件更新したか）
export function masterSyncHistory() {
  return apiGet<{ items: MasterSyncHistoryRecord[] }>('/master-sync/history');
}
